import { Module } from 'vuex';
import { IRootState } from './store';
import restService from '@/services/restService';

export interface IGroupState {
  groups: IGroup[];
  groupUsers: IGroupUserProfile[];
  allGroupUsers: IGroupUserProfile[];
}

const groupState: IGroupState = {
  groups: [],
  groupUsers: [],
  allGroupUsers: [],
};

const groupGetters = {
  isInGroup: (state: IGroupState) => (id: number) => {
    return state.groupUsers.findIndex(gu => gu.userProfileId === id) > -1;
  },
  isMemberOfGroup: (state: IGroupState) => (userId: number, groupId: number) =>
    state.groupUsers.findIndex(gu => gu.userProfileId === userId && gu.groupId === groupId) > -1,
  profilesGroups: (state: IGroupState) => (profileId: number) =>
    state.allGroupUsers
      .filter(gup => gup.userProfileId === profileId)
      .map(fg => state.groups.find(g => g.id === fg.groupId )),
};

const groupMutations = {
  setGroups: (state: IGroupState, groups: IGroup[]) => state.groups = groups,
  setGroupUserProfiles: (state: IGroupState, groupUserProfiles: IGroupUserProfile[]) => {
    state.groupUsers = groupUserProfiles;
  },
  setAllGroupUserProfiles: (state: IGroupState, groupUserProfiles: IGroupUserProfile[]) => {
    state.allGroupUsers = groupUserProfiles;
  },
};

const groupActions = {
  async getGroups({ commit }: {commit: any}) {
    const groups = await restService.groups.getGroups();
    commit('setGroups', groups);
  },
  async getGroupUserProfiles({ commit }: { commit: any }, groupId: number) {
    commit('app/setLoading', true, { root: true });
    const groupUserProfiles = await restService.groups.getGroupUserProfiles(groupId);
    commit('setGroupUserProfiles', groupUserProfiles);
    commit('app/setLoading', false, { root: true });
  },
  async joinGroup({ commit, rootState }: { commit: any, rootState: IRootState }, groupId: number) {
    commit('app/setLoading', true, {root: true});
    if (rootState.user.userProfile) {
      const groupUserProfiles = await restService.groups.postJoinGroup(groupId, rootState.user.userProfile);
      const userGroupProfile = groupUserProfiles.filter(gup => gup.userProfileId === rootState.user.userProfile!.id);
      const userGroups = rootState.user.userProfileGroups.concat(userGroupProfile);
      commit('user/setUserProfileGroups', userGroups, { root: true });
      commit('setGroupUserProfiles', groupUserProfiles);
    }
    commit('app/setLoading', false, { root: true });
  },
  async leaveGroup({ commit, rootState }: { commit: any, rootState: IRootState }, groupId: number) {
    commit('app/setLoading', true, {root: true});
    if (rootState.user.userProfile) {
      const groupUserProfiles = await restService.groups.postLeaveGroup(groupId, rootState.user.userProfile);
      const userGroups = rootState.user.userProfileGroups.filter(upg => upg.groupId !== groupId);
      commit('user/setUserProfileGroups', userGroups, { root: true });
      commit('setGroupUserProfiles', groupUserProfiles);
    }
    commit('app/setLoading', false, { root: true });
  },
  async getAllGroupUserProfiles({ commit }: { commit: any }) {
    commit('app/setLoading', true, { root: true });
    const allGroupUserProfiles = await restService.groups.getAllGroupUserProfiles();
    commit('setAllGroupUserProfiles', allGroupUserProfiles);
    commit('app/setLoading', false, { root: true });
  },
};

const groupStore: Module<IGroupState, IRootState> = {
  namespaced: true,
  state: groupState,
  getters: groupGetters,
  actions: groupActions,
  mutations: groupMutations,
};

export default groupStore;
